import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Descriptions, List, Space, Tag, Typography, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  useSupabaseSession,
  parseUserRole,
  isAdminRole,
  isFleetOwnerRole,
  useFleetOperator,
  usePortalPermissions,
  formatDate,
  formatTime,
  parseClientRequestNotes,
} from '@ubus/shared';
import type { Job, JobLeg, JobLegType, JobSource, JobStatus, JobType } from '@ubus/shared';
import AdminSectionPage from '../components/AdminSectionPage';
import StatusTag from '../components/StatusTag';
import ForbiddenPage from './ForbiddenPage';

const { Text } = Typography;

type JobRow = Job & {
  job_legs: JobLeg[];
  organizations?: { name: string } | null;
};

function JobStatusTag({ status, t }: { status: JobStatus; t: TFunction }) {
  if (status === 'scheduled') {
    return <StatusTag status={status} label={t('jobs.published')} color="success" />;
  }
  return <StatusTag status={status} />;
}

function formatLegTypeLabel(legType: JobLegType, t: TFunction): string {
  if (legType === 'standby_block') return t('jobs.standByBlock');
  if (legType === 'standby_start') return t('jobs.standByStart');
  return t('jobs.transfer');
}

function renderLegSummary(leg: JobLeg, t: TFunction): string {
  if (leg.leg_type === 'standby_block') {
    return `${leg.pickup_address} · ${t('jobs.occupied')} ${formatTime(leg.earliest_start)} – ${leg.end_time ? formatTime(leg.end_time) : '?'}`;
  }
  return `${formatTime(leg.earliest_start)} · ${leg.pickup_address} → ${leg.dropoff_address}`;
}

function legNotesDisplay(leg: JobLeg): string | null {
  const parsed = parseClientRequestNotes(leg.notes);
  const special = parsed.specialNotes?.trim();
  if (special) return special;
  // Standby / free-text legs may store notes without client meta markers.
  if (!parsed.vehiclePreference && parsed.passengerGroups.length === 0) {
    const raw = leg.notes?.trim();
    return raw || null;
  }
  return null;
}

function formatVehiclePreferenceLabel(value: string | null | undefined, t: TFunction): string | null {
  if (!value?.trim()) return null;
  const key = value.trim();
  const map: Record<string, string> = {
    standard: t('jobForm.vehiclePrefStandard'),
    mini: t('jobForm.vehiclePrefMini'),
    coach: t('jobForm.vehiclePrefCoach'),
    any: t('jobForm.vehiclePrefAny'),
  };
  return map[key] ?? key;
}

function clientRequestVehiclePreference(job: JobRow): string | null {
  for (const leg of job.job_legs ?? []) {
    const pref = parseClientRequestNotes(leg.notes).vehiclePreference;
    if (pref) return pref;
  }
  return parseClientRequestNotes(job.notes).vehiclePreference;
}

function formatJobTypeLabel(jobType: JobType, t: TFunction): string {
  return (
    (
      {
        school_run: t('jobs.schoolRun'),
        charter_standby: t('jobs.standBy'),
        tour: t('jobs.tour'),
        transfer: t('jobs.transfer'),
      } as Record<JobType, string>
    )[jobType] ?? jobType
  );
}

function formatSourceLabel(source: JobSource | undefined, t: TFunction): string {
  if (source === 'client_request') return t('jobs.sourceClientRequest');
  if (source === 'fleet') return t('jobs.sourceFleet');
  return t('jobs.sourceHq');
}

function JobDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { loading: permLoading } = usePortalPermissions();
  const { session, loading: authLoading } = useSupabaseSession();
  const user = session?.user ?? null;
  const isHqAdmin = isAdminRole(parseUserRole(user));
  const isFleetOwner = isFleetOwnerRole(parseUserRole(user));
  const { operatorId, loading: fleetLoading } = useFleetOperator();
  const canAccessPage = isHqAdmin || (isFleetOwner && Boolean(operatorId));
  const lockedFleetOperatorId = isHqAdmin ? null : operatorId;
  const gatesLoading = authLoading || fleetLoading || permLoading;

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<JobRow | null>(null);

  useEffect(() => {
    if (gatesLoading || !canAccessPage || !id) return;

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        let query = supabase
          .from('jobs')
          .select('*, job_legs(*), organizations(name)')
          .eq('id', id);
        if (lockedFleetOperatorId) {
          query = query.or(
            `fleet_operator_id.eq.${lockedFleetOperatorId},assist_from_fleet_operator_id.eq.${lockedFleetOperatorId}`
          );
        }
        const { data, error } = await query.maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        if (!data) {
          message.error(t('jobs.notFound'));
          navigate('/jobs', { replace: true });
          return;
        }

        const row = data as JobRow;
        row.job_legs = (row.job_legs ?? []).sort((a, b) => a.sequence - b.sequence);
        setJob(row);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        message.error(err instanceof Error ? err.message : String(err));
        navigate('/jobs', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gatesLoading, canAccessPage, id, lockedFleetOperatorId, navigate, t]);

  if (gatesLoading) {
    return <AdminSectionPage title={t('jobs.detailTitle')} loading />;
  }

  if (!canAccessPage) {
    return <ForbiddenPage />;
  }

  const pageTitle =
    job?.job_number || job?.title
      ? [job.job_number, job.title].filter(Boolean).join(' · ')
      : t('jobs.detailTitle');

  return (
    <AdminSectionPage
      title={pageTitle}
      subtitle={t('jobs.detailTitle')}
      loading={loading}
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/jobs')}>
          {t('jobs.backToList')}
        </Button>
      }
    >
      {job ? (
        <div className="admin-job-form">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, md: 2, lg: 3 }}
            items={[
              {
                key: 'job_number',
                label: t('jobs.columns.jobNumber'),
                children: job.job_number || '—',
              },
              {
                key: 'schedule_date',
                label: t('jobs.columns.date'),
                children: formatDate(job.schedule_date),
              },
              {
                key: 'status',
                label: t('jobs.columns.status'),
                children: <JobStatusTag status={job.status} t={t} />,
              },
              {
                key: 'title',
                label: t('jobs.columns.title'),
                children: job.title,
              },
              {
                key: 'customer',
                label: t('jobs.columns.customer'),
                children: job.organizations?.name?.trim() || '—',
              },
              {
                key: 'type',
                label: t('jobs.columns.type'),
                children: formatJobTypeLabel(job.job_type, t),
              },
              {
                key: 'source',
                label: t('jobs.columns.source'),
                children: formatSourceLabel(job.source, t),
              },
              {
                key: 'pax',
                label: t('jobs.columns.pax'),
                children: job.passenger_count,
              },
              {
                key: 'escort',
                label: t('jobs.columns.fullDayEscort'),
                children: job.driver_full_day_escort ? (
                  <Tag color="purple">{t('jobs.fullDayEscortYes')}</Tag>
                ) : (
                  <Text type="secondary">{t('jobs.fullDayEscortNo')}</Text>
                ),
              },
              {
                key: 'contact',
                label: t('jobForm.contactPerson'),
                children: job.contact_person || '—',
              },
              {
                key: 'phone',
                label: t('jobForm.contactPhone'),
                children: job.contact_phone || '—',
              },
              {
                key: 'vehicle_preference',
                label: t('jobForm.clientVehiclePreference'),
                children:
                  formatVehiclePreferenceLabel(clientRequestVehiclePreference(job), t) || '—',
              },
              {
                key: 'notes',
                label: t('jobForm.notes'),
                children: job.notes || '—',
                span: 3,
              },
            ]}
          />

          <div>
            <Text strong>{t('jobForm.legs')}</Text>
            {(job.job_legs ?? []).length === 0 ? (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{t('jobs.noLegs')}</Text>
              </div>
            ) : (
              <List
                style={{ marginTop: 8 }}
                size="small"
                bordered
                dataSource={job.job_legs}
                renderItem={(leg) => {
                  const notesText = legNotesDisplay(leg);
                  return (
                    <List.Item>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Space wrap>
                          <Text strong>#{leg.sequence}</Text>
                          <Tag color={leg.leg_type === 'standby_block' ? 'orange' : 'blue'}>
                            {formatLegTypeLabel(leg.leg_type, t)}
                          </Tag>
                          <Text>{renderLegSummary(leg, t)}</Text>
                          {leg.service_duration_minutes > 0 && leg.leg_type !== 'standby_block' ? (
                            <Text type="secondary">
                              · {t('jobs.serviceMinutes', { count: leg.service_duration_minutes })}
                            </Text>
                          ) : null}
                        </Space>
                        <Text type="secondary">
                          {t('jobForm.legNotes')}：
                          {notesText ? (
                            <Text style={{ whiteSpace: 'pre-wrap' }}>{notesText}</Text>
                          ) : (
                            '—'
                          )}
                        </Text>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </Space>
        </div>
      ) : null}
    </AdminSectionPage>
  );
}

export default JobDetailPage;
